import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";

import FunctionalButton from "components/layout/FunctionalButton";
import AnimatedModal from "components/layout/motion/AnimatedModal";
import {
    BEHAVIOUR_ACTION_TYPES,
    getBehaviourOperatorLabel,
    createEmptyCondition,
    createEmptyRule,
    isBatteryEventSource,
    isRetryActionType,
    defaultRetryActionParams,
    defaultPlanNextActionParams,
    RETRY_COUNT_OPTIONS,
    RETRY_EXHAUSTED_REGULAR_OPTIONS,
    RETRY_EXHAUSTED_FAILURE_OPTIONS,
    isNavigateCanceledSource,
    isNavigateCanceledCondition,
    isNavigateGoalStatusEnumSource,
    normalizeBehaviourCondition,
    NAVIGATE_GOAL_STATUS_OPTIONS,
} from "lib/consts/behaviourRules";
import {
    FAILURE_POLICY_ACTION_TYPES,
    RETRY_ELIGIBLE_FAILURE_ACTION_TYPES,
    failureActionTypeFromRule,
    failureActionTypeFromSource,
    isMissionStepFailureCondition,
    isMissionStepFailureSource,
} from "lib/consts/missionStepFailure";
import {
    MISSION_LIGHT_ACTION_TYPES,
    MISSION_LIGHT_TYPES,
    MISSION_LIGHTS_RULE_ID,
    MISSION_LIGHTS_TOPIC,
    isMissionLightsCondition,
    isMissionLightsSource,
    lightTypesFromRule,
} from "lib/consts/missionLights";
import {
    MISSION_AUDIO_ACTION_TYPE,
    MISSION_AUDIO_INTERVAL_DEFAULT,
    MISSION_AUDIO_INTERVAL_MAX,
    MISSION_AUDIO_INTERVAL_MIN,
    MISSION_AUDIO_RULE_ID,
    MISSION_AUDIO_TOPIC,
    MISSION_AUDIO_VOLUME_DEFAULT,
    isMissionAudioCondition,
    isMissionAudioSource,
} from "lib/consts/missionAudio";
import { RETURN_TO_LAUNCH_MODE_OPTIONS } from "lib/consts/missionBehaviour";
import { listAudioTracks } from "lib/audioTracksApi";

import { ReactComponent as CloseIcon } from "assets/svgs/Minus.svg";

const cloneRule = (rule) => JSON.parse(JSON.stringify(rule));

const BehaviourRuleDialog = ({ visible, initialRule, catalog, onClose, onSave, isSaving }) => {
    const { arcId, arcProxyHost } = useSelector((state) => state.arcProxy);
    const [draft, setDraft] = useState(createEmptyRule());
    const [error, setError] = useState("");
    const [audioTracks, setAudioTracks] = useState([]);
    const [audioTracksLoading, setAudioTracksLoading] = useState(false);

    useEffect(() => {
        if (!visible) return;
        const rule = initialRule ? cloneRule(initialRule) : createEmptyRule();
        if (rule.isMissionLights || isMissionLightsCondition(rule.conditions?.items?.[0])) {
            rule.isMissionLights = true;
            rule.selectedLightTypes = lightTypesFromRule(rule);
            if (!rule.selectedLightTypes?.length) {
                rule.selectedLightTypes = [...MISSION_LIGHT_TYPES];
            }
            rule.action = {
                type: MISSION_LIGHT_ACTION_TYPES.COMBINED,
                params: { lights: rule.selectedLightTypes.join(",") },
            };
        }
        if (rule.isMissionAudio || isMissionAudioCondition(rule.conditions?.items?.[0])) {
            rule.isMissionAudio = true;
            rule.action = {
                type: MISSION_AUDIO_ACTION_TYPE,
                params: {
                    track_id: rule.action?.params?.track_id || "",
                    interval_sec:
                        rule.action?.params?.interval_sec ||
                        String(MISSION_AUDIO_INTERVAL_DEFAULT),
                    volume:
                        rule.action?.params?.volume || String(MISSION_AUDIO_VOLUME_DEFAULT),
                    track_name: rule.action?.params?.track_name || "",
                },
            };
        }
        rule.conditions.items = (rule.conditions.items || []).map((condition) => {
            const source = catalog?.event_sources?.find(
                (item) => item.topic === condition.topic && item.field === condition.field
            );
            if (
                isMissionStepFailureSource(source) ||
                isMissionStepFailureCondition(condition) ||
                rule.isFailurePolicy ||
                isMissionLightsSource(source) ||
                isMissionLightsCondition(condition) ||
                rule.isMissionLights ||
                isMissionAudioSource(source) ||
                isMissionAudioCondition(condition) ||
                rule.isMissionAudio
            ) {
                return condition;
            }
            const normalized = normalizeBehaviourCondition(condition, source);
            if (
                normalized.topic === "/state/battery" &&
                !["lt", "gt"].includes(normalized.operator)
            ) {
                return { ...normalized, operator: "lt" };
            }
            return normalized;
        });
        setDraft(rule);
        setError("");
    }, [visible, initialRule, catalog?.event_sources]);

    const eventSources = catalog?.event_sources ?? [];
    const actions = catalog?.actions ?? [];

    const primaryCondition = draft.conditions.items[0];
    const primarySource = eventSources.find(
        (item) =>
            item.topic === primaryCondition?.topic && item.field === primaryCondition?.field,
    );
    const isFailureRule =
        isMissionStepFailureSource(primarySource) ||
        isMissionStepFailureCondition(primaryCondition) ||
        Boolean(draft.isFailurePolicy);
    const isMissionLightsRuleDraft =
        isMissionLightsSource(primarySource) ||
        isMissionLightsCondition(primaryCondition) ||
        Boolean(draft.isMissionLights);
    const isMissionAudioRuleDraft =
        isMissionAudioSource(primarySource) ||
        isMissionAudioCondition(primaryCondition) ||
        Boolean(draft.isMissionAudio);
    const failureActionType = failureActionTypeFromSource(primarySource) ||
        draft.failureActionType ||
        failureActionTypeFromRule(draft, eventSources);
    const isBatteryCondition = isBatteryEventSource(primarySource);

    useEffect(() => {
        if (!visible || !isMissionAudioRuleDraft) return;
        let cancelled = false;
        setAudioTracksLoading(true);
        listAudioTracks(arcId, arcProxyHost)
            .then((tracks) => {
                if (!cancelled) setAudioTracks(tracks);
            })
            .catch(() => {
                if (!cancelled) setError("Failed to load audio tracks.");
            })
            .finally(() => {
                if (!cancelled) setAudioTracksLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [visible, isMissionAudioRuleDraft, arcId, arcProxyHost]);

    const availableActions = useMemo(() => {
        if (isMissionLightsRuleDraft) {
            return actions.filter(
                (action) =>
                    action.for_mission_lights &&
                    action.type === MISSION_LIGHT_ACTION_TYPES.COMBINED,
            );
        }
        if (isMissionAudioRuleDraft) {
            return actions.filter(
                (action) =>
                    action.for_mission_audio &&
                    action.type === MISSION_AUDIO_ACTION_TYPE,
            );
        }
        if (isFailureRule) {
            return actions.filter((action) => {
                if (!action.for_mission_step_failure) return false;
                if (action.type === FAILURE_POLICY_ACTION_TYPES.RETRY) {
                    return RETRY_ELIGIBLE_FAILURE_ACTION_TYPES.includes(failureActionType);
                }
                if (action.for_failure_action_types?.length) {
                    return action.for_failure_action_types.includes(failureActionType);
                }
                return true;
            });
        }
        return actions.filter((action) => {
            if (action.for_mission_step_failure) return false;
            if (action.for_mission_lights) return false;
            if (action.for_mission_audio) return false;
            if (
                action.type === BEHAVIOUR_ACTION_TYPES.RETRY_FAILED_ACTION &&
                isBatteryCondition
            ) {
                return false;
            }
            if (
                primarySource?.id &&
                action.exclude_source_ids?.includes(primarySource.id)
            ) {
                return false;
            }
            return true;
        });
    }, [
        actions,
        isFailureRule,
        isMissionLightsRuleDraft,
        isMissionAudioRuleDraft,
        isBatteryCondition,
        failureActionType,
        primarySource?.id,
    ]);

    const missionLightOptions = useMemo(() => {
        const fromCatalog = catalog?.mission_light_types ?? [];
        if (fromCatalog.length > 0) {
            return fromCatalog.map((item) => ({
                value: item.light_type,
                label: item.label,
            }));
        }
        return [
            { value: "spotlight", label: "Spotlight" },
            { value: "flash", label: "Flash" },
        ];
    }, [catalog?.mission_light_types]);

    const selectedLightTypes = draft.selectedLightTypes ?? [];

    const toggleSelectedLight = (lightType) => {
        setDraft((prev) => {
            const current = new Set(prev.selectedLightTypes || []);
            if (current.has(lightType)) {
                current.delete(lightType);
            } else {
                current.add(lightType);
            }
            const nextSelected = MISSION_LIGHT_TYPES.filter((item) => current.has(item));
            return {
                ...prev,
                selectedLightTypes: nextSelected,
                action: {
                    type: MISSION_LIGHT_ACTION_TYPES.COMBINED,
                    params: { lights: nextSelected.join(",") },
                },
            };
        });
    };

    const retryExhaustedOptions = isFailureRule
        ? RETRY_EXHAUSTED_FAILURE_OPTIONS
        : RETRY_EXHAUSTED_REGULAR_OPTIONS;
    const showRetryExhaustedMode =
        isRetryActionType(draft.action.type) &&
        (draft.action.params?.on_exhausted === "return_to_launch" ||
            draft.action.params?.on_exhausted === "rtl");

    const buildActionParamsForType = (actionType) => {
        if (actionType === BEHAVIOUR_ACTION_TYPES.RETURN_TO_LAUNCH) {
            return { mode: "plan_to_launch" };
        }
        if (actionType === FAILURE_POLICY_ACTION_TYPES.RTL) {
            return { mode: "plan_to_launch" };
        }
        if (actionType === FAILURE_POLICY_ACTION_TYPES.PLAN_NEXT) {
            return defaultPlanNextActionParams();
        }
        if (isRetryActionType(actionType)) {
            return defaultRetryActionParams(isFailureRule);
        }
        return {};
    };

    const selectedAction = useMemo(
        () => availableActions.find((item) => item.type === draft.action.type),
        [availableActions, draft.action.type],
    );

    useEffect(() => {
        if (!visible) return;
        if (isMissionLightsRuleDraft) {
            if (draft.action.type === MISSION_LIGHT_ACTION_TYPES.COMBINED) return;
            setDraft((prev) => ({
                ...prev,
                action: {
                    type: MISSION_LIGHT_ACTION_TYPES.COMBINED,
                    params: {
                        lights: (prev.selectedLightTypes || []).join(","),
                    },
                },
            }));
            return;
        }
        if (isMissionAudioRuleDraft) {
            if (draft.action.type === MISSION_AUDIO_ACTION_TYPE) return;
            setDraft((prev) => ({
                ...prev,
                action: {
                    type: MISSION_AUDIO_ACTION_TYPE,
                    params: {
                        track_id: prev.action?.params?.track_id || "",
                        interval_sec:
                            prev.action?.params?.interval_sec ||
                            String(MISSION_AUDIO_INTERVAL_DEFAULT),
                        volume:
                            prev.action?.params?.volume ||
                            String(MISSION_AUDIO_VOLUME_DEFAULT),
                        track_name: prev.action?.params?.track_name || "",
                    },
                },
            }));
            return;
        }
        if (availableActions.some((action) => action.type === draft.action.type)) return;
        setDraft((prev) => ({
            ...prev,
            action: {
                type: isFailureRule
                    ? FAILURE_POLICY_ACTION_TYPES.STOP
                    : BEHAVIOUR_ACTION_TYPES.RETURN_TO_LAUNCH,
                params: isFailureRule ? {} : { mode: "plan_to_launch" },
            },
        }));
    }, [
        visible,
        isFailureRule,
        isMissionLightsRuleDraft,
        isMissionAudioRuleDraft,
        availableActions,
        draft.action.type,
    ]);

    const updateAudioParam = (key, value) => {
        setDraft((prev) => ({
            ...prev,
            action: {
                ...prev.action,
                type: MISSION_AUDIO_ACTION_TYPE,
                params: { ...prev.action.params, [key]: value },
            },
        }));
    };

    const updateCondition = (index, patch) => {
        setDraft((prev) => {
            const next = cloneRule(prev);
            next.conditions.items[index] = { ...next.conditions.items[index], ...patch };
            return next;
        });
    };

    const handleEventSourceChange = (index, sourceId) => {
        const source = eventSources.find((item) => item.id === sourceId);
        if (!source) return;

        if (isMissionStepFailureSource(source)) {
            const failureActionType = failureActionTypeFromSource(source);
            setDraft((prev) => ({
                ...prev,
                isFailurePolicy: true,
                failureActionType,
                conditions: {
                    logic: "and",
                    items: [
                        {
                            source: "mission_step_failure",
                            topic: source.topic,
                            field: source.field,
                            operator: "eq",
                            value: "true",
                        },
                    ],
                },
                action: {
                    type: FAILURE_POLICY_ACTION_TYPES.STOP,
                    params: {},
                },
            }));
            return;
        }

        if (isMissionLightsSource(source)) {
            setDraft((prev) => {
                const next = {
                    ...prev,
                    isMissionLights: true,
                    selectedLightTypes: [...MISSION_LIGHT_TYPES],
                    name: prev.name === "New rule" ? "Lights during mission" : prev.name,
                    conditions: {
                        logic: "and",
                        items: [
                            {
                                source: "mission_lights",
                                topic: MISSION_LIGHTS_TOPIC,
                                field: "mission_running",
                                operator: "eq",
                                value: "true",
                            },
                        ],
                    },
                    action: {
                        type: MISSION_LIGHT_ACTION_TYPES.COMBINED,
                        params: { lights: MISSION_LIGHT_TYPES.join(",") },
                    },
                };
                delete next.isFailurePolicy;
                delete next.failureActionType;
                delete next.isMissionAudio;
                return next;
            });
            return;
        }

        if (isMissionAudioSource(source)) {
            setDraft((prev) => {
                const next = {
                    ...prev,
                    isMissionAudio: true,
                    name: prev.name === "New rule" ? "Audio during mission" : prev.name,
                    conditions: {
                        logic: "and",
                        items: [
                            {
                                source: "mission_audio",
                                topic: MISSION_AUDIO_TOPIC,
                                field: "mission_running",
                                operator: "eq",
                                value: "true",
                            },
                        ],
                    },
                    action: {
                        type: MISSION_AUDIO_ACTION_TYPE,
                        params: {
                            track_id: prev.action?.params?.track_id || "",
                            interval_sec: String(MISSION_AUDIO_INTERVAL_DEFAULT),
                            volume: String(MISSION_AUDIO_VOLUME_DEFAULT),
                            track_name: prev.action?.params?.track_name || "",
                        },
                    },
                };
                delete next.isFailurePolicy;
                delete next.failureActionType;
                delete next.isMissionLights;
                delete next.selectedLightTypes;
                return next;
            });
            return;
        }

        setDraft((prev) => {
            const next = cloneRule(prev);
            delete next.isFailurePolicy;
            delete next.failureActionType;
            delete next.isMissionLights;
            delete next.isMissionAudio;
            delete next.lightType;
            delete next.lightTypes;
            delete next.selectedLightTypes;
            return next;
        });

        if (isNavigateCanceledSource(source)) {
            updateCondition(index, {
                source: "ros_topic",
                topic: source.topic,
                field: source.field,
                operator: "eq",
                value: source.value_options?.[0]?.value ?? "true",
            });
            return;
        }

        if (isNavigateGoalStatusEnumSource(source)) {
            updateCondition(index, {
                source: "ros_topic",
                topic: source.topic,
                field: source.field,
                operator: "is",
                value: NAVIGATE_GOAL_STATUS_OPTIONS[0].value,
            });
            return;
        }

        updateCondition(index, {
            source: "ros_topic",
            topic: source.topic,
            field: source.field,
            operator: source.operators[0] ?? "lt",
            value: "",
        });
    };

    const handleSave = async () => {
        if (!draft.name.trim()) {
            setError("Rule name is required.");
            return;
        }
        if (!draft.conditions.items.length) {
            setError("At least one condition is required.");
            return;
        }
        if (isMissionLightsRuleDraft) {
            const selected = lightTypesFromRule({
                lightTypes: draft.selectedLightTypes,
                action: {
                    type: MISSION_LIGHT_ACTION_TYPES.COMBINED,
                    params: { lights: (draft.selectedLightTypes || []).join(",") },
                },
            });
            if (selected.length === 0) {
                setError("Select at least one light.");
                return;
            }
        } else if (isMissionAudioRuleDraft) {
            const trackId = String(draft.action.params?.track_id || "").trim();
            if (!trackId) {
                setError("Select an audio track (upload in Payload → Patrol Module settings).");
                return;
            }
            const intervalSec = Number(draft.action.params?.interval_sec);
            if (
                !Number.isFinite(intervalSec) ||
                intervalSec < MISSION_AUDIO_INTERVAL_MIN ||
                intervalSec > MISSION_AUDIO_INTERVAL_MAX
            ) {
                setError(
                    `Interval must be between ${MISSION_AUDIO_INTERVAL_MIN} and ${MISSION_AUDIO_INTERVAL_MAX} seconds.`,
                );
                return;
            }
            const volume = Number(draft.action.params?.volume);
            if (!Number.isFinite(volume) || volume < 0 || volume > 100) {
                setError("Volume must be between 0 and 100.");
                return;
            }
        } else if (!draft.action.type) {
            setError("Select a behaviour action.");
            return;
        }
        if (isRetryActionType(draft.action.type)) {
            const retryCount = String(draft.action.params?.retry_count || "").trim();
            const onExhausted = String(draft.action.params?.on_exhausted || "").trim();
            if (!RETRY_COUNT_OPTIONS.includes(retryCount)) {
                setError("Select how many times to retry.");
                return;
            }
            if (!onExhausted) {
                setError("Select what to do if all retries fail.");
                return;
            }
        }
        if (draft.action.type === FAILURE_POLICY_ACTION_TYPES.PLAN_NEXT) {
            const planNextMax = Number(String(draft.action.params?.plan_next_max || "").trim());
            if (!Number.isInteger(planNextMax) || planNextMax < 1) {
                setError("Max consecutive skips must be a whole number of at least 1.");
                return;
            }
        }
        if (!isFailureRule && !isMissionLightsRuleDraft && !isMissionAudioRuleDraft) {
            for (const condition of draft.conditions.items) {
                if (!condition.topic || !condition.field || !String(condition.value).trim()) {
                    setError("Each condition needs a value.");
                    return;
                }
            }
        }
        setError("");
        const selectedLights = isMissionLightsRuleDraft
            ? lightTypesFromRule({
                  lightTypes: draft.selectedLightTypes,
                  action: {
                      type: MISSION_LIGHT_ACTION_TYPES.COMBINED,
                      params: { lights: (draft.selectedLightTypes || []).join(",") },
                  },
              })
            : [];
        const selectedTrack = audioTracks.find(
            (track) => track.id === draft.action.params?.track_id,
        );
        const normalizedRule = {
            ...draft,
            ...(isMissionLightsRuleDraft
                ? {
                      isMissionLights: true,
                      lightTypes: selectedLights,
                      id: MISSION_LIGHTS_RULE_ID,
                      action: {
                          type: MISSION_LIGHT_ACTION_TYPES.COMBINED,
                          params: { lights: selectedLights.join(",") },
                      },
                  }
                : {}),
            ...(isMissionAudioRuleDraft
                ? {
                      isMissionAudio: true,
                      id: MISSION_AUDIO_RULE_ID,
                      action: {
                          type: MISSION_AUDIO_ACTION_TYPE,
                          params: {
                              track_id: String(draft.action.params?.track_id || "").trim(),
                              interval_sec: String(draft.action.params?.interval_sec || ""),
                              volume: String(draft.action.params?.volume || ""),
                              track_name:
                                  selectedTrack?.name ||
                                  draft.action.params?.track_name ||
                                  "",
                          },
                      },
                  }
                : {}),
            conditions: {
                ...draft.conditions,
                items: draft.conditions.items.map((condition) => {
                    const source = eventSources.find(
                        (item) => item.topic === condition.topic && item.field === condition.field
                    );
                    if (
                        isMissionStepFailureSource(source) ||
                        isMissionStepFailureCondition(condition) ||
                        isMissionLightsSource(source) ||
                        isMissionLightsCondition(condition) ||
                        isMissionAudioSource(source) ||
                        isMissionAudioCondition(condition)
                    ) {
                        return condition;
                    }
                    return normalizeBehaviourCondition(condition, source);
                }),
            },
        };
        await onSave(normalizedRule);
    };

    return (
        <AnimatedModal
            visible={visible}
            onClose={onClose}
            className="mx-auto w-full max-w-[32rem] rounded-r5 border border-black70 bg-black100 p-4 shadow-lg"
            zIndexClassName="z-[200]"
        >
            <div className="mb-3 flex items-center justify-between">
                <p className="text-h4 font-semibold">{initialRule ? "Edit Rule" : "Add Rule"}</p>
                <FunctionalButton
                    icon={<CloseIcon className="w-4" />}
                    className="h-6 w-6 rounded-r3 hover:bg-black70"
                    onClick={onClose}
                />
            </div>

            <div className="space-y-3">
                <div>
                    <p className="text-h5 mb-1 text-black40">Rule name</p>
                    <input
                        className="text-h5 w-full rounded-r5 border border-black70 bg-black90 px-3 py-2"
                        value={draft.name}
                        onChange={(e) => setDraft((prev) => ({ ...prev, name: e.target.value }))}
                    />
                </div>

                <div className="space-y-2">
                    <p className="text-h5 font-semibold">If</p>
                    {draft.conditions.items.map((condition, index) => {
                        const source = eventSources.find(
                            (item) =>
                                item.topic === condition.topic && item.field === condition.field
                        );
                        const isCanceledNav =
                            isNavigateCanceledSource(source) ||
                            isNavigateCanceledCondition(condition);
                        const isStepFailure =
                            isMissionStepFailureSource(source) ||
                            isMissionStepFailureCondition(condition);
                        const isMissionLights =
                            isMissionLightsSource(source) ||
                            isMissionLightsCondition(condition);
                        const isMissionAudio =
                            isMissionAudioSource(source) ||
                            isMissionAudioCondition(condition);
                        const isEnumNav = isNavigateGoalStatusEnumSource(source);
                        const statusOptions =
                            source?.value_options?.length > 0
                                ? source.value_options
                                : NAVIGATE_GOAL_STATUS_OPTIONS;
                        return (
                            <div
                                key={`condition-${index}`}
                                className="space-y-2 rounded-r5 border border-black70 p-3"
                            >
                                <select
                                    className="text-h5 w-full rounded-r5 border border-black70 bg-black90 px-3 py-2"
                                    value={source?.id ?? ""}
                                    onChange={(e) => handleEventSourceChange(index, e.target.value)}
                                >
                                    <option value="" disabled>
                                        Select event
                                    </option>
                                    {eventSources.map((item) => (
                                        <option key={item.id} value={item.id}>
                                            {item.label}
                                        </option>
                                    ))}
                                </select>
                                {isStepFailure || isMissionLights || isMissionAudio || isCanceledNav ? null : isEnumNav ? (
                                    <select
                                        className="text-h5 w-full rounded-r5 border border-black70 bg-black90 px-3 py-2"
                                        value={condition.value}
                                        onChange={(e) =>
                                            updateCondition(index, {
                                                operator: "is",
                                                value: e.target.value,
                                            })
                                        }
                                    >
                                        {statusOptions.map((option) => (
                                            <option key={option.value} value={option.value}>
                                                {option.label}
                                            </option>
                                        ))}
                                    </select>
                                ) : (
                                    <>
                                        <select
                                            className="text-h5 w-full rounded-r5 border border-black70 bg-black90 px-3 py-2"
                                            value={condition.operator}
                                            onChange={(e) =>
                                                updateCondition(index, { operator: e.target.value })
                                            }
                                        >
                                            {(source?.operators ?? ["lt", "gt"]).map((operator) => (
                                                <option key={operator} value={operator}>
                                                    {getBehaviourOperatorLabel(
                                                        source?.id,
                                                        operator
                                                    )}
                                                </option>
                                            ))}
                                        </select>
                                        <input
                                            className="text-h5 w-full rounded-r5 border border-black70 bg-black90 px-3 py-2"
                                            placeholder="Value"
                                            value={condition.value}
                                            onChange={(e) =>
                                                updateCondition(index, { value: e.target.value })
                                            }
                                        />
                                    </>
                                )}
                                {!isFailureRule &&
                                    !isMissionLightsRuleDraft &&
                                    !isMissionAudioRuleDraft &&
                                    draft.conditions.items.length > 1 && (
                                    <button
                                        type="button"
                                        className="text-h5 text-warning"
                                        onClick={() =>
                                            setDraft((prev) => {
                                                const next = cloneRule(prev);
                                                next.conditions.items =
                                                    next.conditions.items.filter(
                                                        (_, itemIndex) => itemIndex !== index
                                                    );
                                                return next;
                                            })
                                        }
                                    >
                                        Remove condition
                                    </button>
                                )}
                            </div>
                        );
                    })}
                    {!isFailureRule && !isMissionLightsRuleDraft && !isMissionAudioRuleDraft && (
                        <FunctionalButton
                            title="Add condition"
                            className="text-h5 h-8 rounded-r5 border border-black70 bg-black90 px-3"
                            onClick={() =>
                                setDraft((prev) => {
                                    const next = cloneRule(prev);
                                    next.conditions.items.push(createEmptyCondition());
                                    return next;
                                })
                            }
                        />
                    )}
                </div>

                <div className="space-y-2">
                    <p className="text-h5 font-semibold">Then</p>
                    {isMissionLightsRuleDraft ? (
                        <div className="space-y-2 rounded-r5 border border-black70 p-3">
                            <p className="text-h5 text-black40">Turn on during mission</p>
                            {missionLightOptions.map((option) => {
                                const checked = selectedLightTypes.includes(option.value);
                                return (
                                    <label
                                        key={option.value}
                                        className="flex cursor-pointer items-center gap-2 text-h5"
                                    >
                                        <input
                                            type="checkbox"
                                            className="h-4 w-4 accent-primary2"
                                            checked={checked}
                                            onChange={() => toggleSelectedLight(option.value)}
                                        />
                                        <span>{option.label}</span>
                                    </label>
                                );
                            })}
                        </div>
                    ) : isMissionAudioRuleDraft ? (
                        <div className="space-y-2 rounded-r5 border border-black70 p-3">
                            <p className="text-h5 text-black40">Play during mission</p>
                            <select
                                className="text-h5 w-full rounded-r5 border border-black70 bg-black90 px-3 py-2"
                                value={draft.action.params?.track_id ?? ""}
                                onChange={(e) => {
                                    const track = audioTracks.find(
                                        (item) => item.id === e.target.value,
                                    );
                                    setDraft((prev) => ({
                                        ...prev,
                                        name:
                                            prev.name === "New rule" ||
                                            prev.name === "Audio during mission"
                                                ? track?.name || prev.name
                                                : prev.name,
                                        action: {
                                            type: MISSION_AUDIO_ACTION_TYPE,
                                            params: {
                                                ...prev.action.params,
                                                track_id: e.target.value,
                                                track_name: track?.name || "",
                                            },
                                        },
                                    }));
                                }}
                            >
                                <option value="" disabled>
                                    {audioTracksLoading
                                        ? "Loading tracks..."
                                        : audioTracks.length === 0
                                          ? "No tracks — upload in Payload settings"
                                          : "Select track"}
                                </option>
                                {audioTracks.map((track) => (
                                    <option key={track.id} value={track.id}>
                                        {track.name}
                                    </option>
                                ))}
                            </select>
                            <p className="text-h5 text-black40">
                                Upload tracks in Payload → Patrol Module → Settings.
                            </p>
                            <div>
                                <p className="text-h5 mb-1 text-black40">
                                    Interval (seconds)
                                </p>
                                <input
                                    type="number"
                                    min={MISSION_AUDIO_INTERVAL_MIN}
                                    max={MISSION_AUDIO_INTERVAL_MAX}
                                    className="text-h5 w-full rounded-r5 border border-black70 bg-black90 px-3 py-2"
                                    value={draft.action.params?.interval_sec ?? ""}
                                    onChange={(e) =>
                                        updateAudioParam("interval_sec", e.target.value)
                                    }
                                />
                            </div>
                            <div>
                                <p className="text-h5 mb-1 text-black40">
                                    Volume ({draft.action.params?.volume ?? 80}%)
                                </p>
                                <input
                                    type="range"
                                    min={0}
                                    max={100}
                                    className="w-full accent-primary2"
                                    value={Number(draft.action.params?.volume ?? 80)}
                                    onChange={(e) =>
                                        updateAudioParam("volume", e.target.value)
                                    }
                                />
                            </div>
                        </div>
                    ) : (
                        <>
                            <select
                                className="text-h5 w-full rounded-r5 border border-black70 bg-black90 px-3 py-2"
                                value={draft.action.type}
                                onChange={(e) =>
                                    setDraft((prev) => ({
                                        ...prev,
                                        action: {
                                            type: e.target.value,
                                            params: buildActionParamsForType(e.target.value),
                                        },
                                    }))
                                }
                            >
                                {availableActions.map((action) => (
                                    <option key={action.type} value={action.type}>
                                        {action.label}
                                    </option>
                                ))}
                            </select>
                            {draft.action.type === BEHAVIOUR_ACTION_TYPES.RETURN_TO_LAUNCH && (
                                <select
                                    className="text-h5 w-full rounded-r5 border border-black70 bg-black90 px-3 py-2"
                                    value={draft.action.params?.mode ?? "plan_to_launch"}
                                    onChange={(e) =>
                                        setDraft((prev) => ({
                                            ...prev,
                                            action: {
                                                ...prev.action,
                                                params: { ...prev.action.params, mode: e.target.value },
                                            },
                                        }))
                                    }
                                >
                                    {RETURN_TO_LAUNCH_MODE_OPTIONS.map((option) => (
                                        <option key={option.value} value={option.value}>
                                            {option.label}
                                        </option>
                                    ))}
                                </select>
                            )}
                            {draft.action.type === FAILURE_POLICY_ACTION_TYPES.RTL && (
                                <select
                                    className="text-h5 w-full rounded-r5 border border-black70 bg-black90 px-3 py-2"
                                    value={draft.action.params?.mode ?? "plan_to_launch"}
                                    onChange={(e) =>
                                        setDraft((prev) => ({
                                            ...prev,
                                            action: {
                                                ...prev.action,
                                                params: { ...prev.action.params, mode: e.target.value },
                                            },
                                        }))
                                    }
                                >
                                    {RETURN_TO_LAUNCH_MODE_OPTIONS.map((option) => (
                                        <option key={option.value} value={option.value}>
                                            {option.label}
                                        </option>
                                    ))}
                                </select>
                            )}
                            {isRetryActionType(draft.action.type) && (
                                <>
                                    <div>
                                        <p className="text-h5 mb-1 text-black40">Retry attempts</p>
                                        <select
                                            className="text-h5 w-full rounded-r5 border border-black70 bg-black90 px-3 py-2"
                                            value={draft.action.params?.retry_count ?? "1"}
                                            onChange={(e) =>
                                                setDraft((prev) => ({
                                                    ...prev,
                                                    action: {
                                                        ...prev.action,
                                                        params: {
                                                            ...prev.action.params,
                                                            retry_count: e.target.value,
                                                        },
                                                    },
                                                }))
                                            }
                                        >
                                            {RETRY_COUNT_OPTIONS.map((value) => (
                                                <option key={value} value={value}>
                                                    {value}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                    <div>
                                        <p className="text-h5 mb-1 text-black40">If all retries fail</p>
                                        <select
                                            className="text-h5 w-full rounded-r5 border border-black70 bg-black90 px-3 py-2"
                                            value={
                                                draft.action.params?.on_exhausted ??
                                                retryExhaustedOptions[0].value
                                            }
                                            onChange={(e) =>
                                                setDraft((prev) => ({
                                                    ...prev,
                                                    action: {
                                                        ...prev.action,
                                                        params: {
                                                            ...prev.action.params,
                                                            on_exhausted: e.target.value,
                                                        },
                                                    },
                                                }))
                                            }
                                        >
                                            {retryExhaustedOptions.map((option) => (
                                                <option key={option.value} value={option.value}>
                                                    {option.label}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                </>
                            )}
                            {showRetryExhaustedMode && (
                                <select
                                    className="text-h5 w-full rounded-r5 border border-black70 bg-black90 px-3 py-2"
                                    value={draft.action.params?.mode ?? "plan_to_launch"}
                                    onChange={(e) =>
                                        setDraft((prev) => ({
                                            ...prev,
                                            action: {
                                                ...prev.action,
                                                params: { ...prev.action.params, mode: e.target.value },
                                            },
                                        }))
                                    }
                                >
                                    {RETURN_TO_LAUNCH_MODE_OPTIONS.map((option) => (
                                        <option key={option.value} value={option.value}>
                                            {option.label}
                                        </option>
                                    ))}
                                </select>
                            )}
                            {draft.action.type === FAILURE_POLICY_ACTION_TYPES.PLAN_NEXT && (
                                <div>
                                    <p className="text-h5 mb-1 text-black40">
                                        Max consecutive skips
                                    </p>
                                    <div className="flex items-center gap-3">
                                        <FunctionalButton
                                            title="-"
                                            className="flex h-8 w-8 items-center justify-center"
                                            disabled={
                                                Number(draft.action.params?.plan_next_max) <= 1
                                            }
                                            onClick={() => {
                                                const current = Number(
                                                    draft.action.params?.plan_next_max,
                                                );
                                                const next = Number.isFinite(current)
                                                    ? Math.max(1, current - 1)
                                                    : 1;
                                                setDraft((prev) => ({
                                                    ...prev,
                                                    action: {
                                                        ...prev.action,
                                                        params: {
                                                            ...prev.action.params,
                                                            plan_next_max: String(next),
                                                        },
                                                    },
                                                }));
                                            }}
                                        />
                                        <input
                                            type="number"
                                            min={1}
                                            inputMode="numeric"
                                            className="text-h5 w-20 rounded-r5 border border-black70 bg-black90 px-2 py-2 text-center"
                                            value={draft.action.params?.plan_next_max ?? ""}
                                            onChange={(e) => {
                                                const raw = e.target.value;
                                                // Allow clearing while typing; save validates >= 1.
                                                if (raw === "") {
                                                    setDraft((prev) => ({
                                                        ...prev,
                                                        action: {
                                                            ...prev.action,
                                                            params: {
                                                                ...prev.action.params,
                                                                plan_next_max: "",
                                                            },
                                                        },
                                                    }));
                                                    return;
                                                }
                                                if (!/^\d+$/.test(raw)) return;
                                                setDraft((prev) => ({
                                                    ...prev,
                                                    action: {
                                                        ...prev.action,
                                                        params: {
                                                            ...prev.action.params,
                                                            plan_next_max: raw.replace(
                                                                /^0+(?=\d)/,
                                                                "",
                                                            ),
                                                        },
                                                    },
                                                }));
                                            }}
                                        />
                                        <FunctionalButton
                                            title="+"
                                            className="flex h-8 w-8 items-center justify-center"
                                            onClick={() => {
                                                const current = Number(
                                                    draft.action.params?.plan_next_max,
                                                );
                                                const next = Number.isFinite(current)
                                                    ? current + 1
                                                    : 1;
                                                setDraft((prev) => ({
                                                    ...prev,
                                                    action: {
                                                        ...prev.action,
                                                        params: {
                                                            ...prev.action.params,
                                                            plan_next_max: String(next),
                                                        },
                                                    },
                                                }));
                                            }}
                                        />
                                    </div>
                                </div>
                            )}
                            {selectedAction?.params?.length > 0 &&
                                !isRetryActionType(draft.action.type) &&
                                draft.action.type !== BEHAVIOUR_ACTION_TYPES.RETURN_TO_LAUNCH &&
                                draft.action.type !== FAILURE_POLICY_ACTION_TYPES.RTL &&
                                selectedAction.params
                                    .filter((param) => {
                                        if (param.input === "checkbox") return false;
                                        if (param.input === "number") return false;
                                        // Dedicated stepper UI; also ignore legacy catalog options 1–5.
                                        if (param.name === "plan_next_max") return false;
                                        if (!param.options?.length) return false;
                                        if (!param.when_param || !param.when_value) return true;
                                        return (
                                            draft.action.params?.[param.when_param] ===
                                            param.when_value
                                        );
                                    })
                                    .map((param) => (
                                        <div key={param.name}>
                                            <p className="text-h5 mb-1 text-black40">{param.label}</p>
                                            <select
                                                className="text-h5 w-full rounded-r5 border border-black70 bg-black90 px-3 py-2"
                                                value={draft.action.params?.[param.name] ?? ""}
                                                onChange={(e) =>
                                                    setDraft((prev) => ({
                                                        ...prev,
                                                        action: {
                                                            ...prev.action,
                                                            params: {
                                                                ...prev.action.params,
                                                                [param.name]: e.target.value,
                                                            },
                                                        },
                                                    }))
                                                }
                                            >
                                                {(param.options ?? []).map((option) => (
                                                    <option key={option.value} value={option.value}>
                                                        {option.label}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                    ))}
                            {draft.action.type === FAILURE_POLICY_ACTION_TYPES.PLAN_NEXT && (
                                <label className="flex cursor-pointer items-center gap-2 text-h5">
                                    <input
                                        type="checkbox"
                                        className="h-4 w-4 accent-primary2"
                                        checked={
                                            draft.action.params?.skip_follow_on_actions !==
                                            "false"
                                        }
                                        onChange={(e) =>
                                            setDraft((prev) => ({
                                                ...prev,
                                                action: {
                                                    ...prev.action,
                                                    params: {
                                                        ...prev.action.params,
                                                        skip_follow_on_actions: e.target
                                                            .checked
                                                            ? "true"
                                                            : "false",
                                                    },
                                                },
                                            }))
                                        }
                                    />
                                    <span>
                                        Also skip Take photo and Wait after skipped waypoint
                                    </span>
                                </label>
                            )}
                        </>
                    )}
                </div>

                {error && <p className="text-h5 text-warning">{error}</p>}

                <div className="flex justify-end gap-2">
                    <FunctionalButton
                        title="Cancel"
                        className="text-h5 h-8 rounded-r5 border border-black70 bg-black90 px-4"
                        onClick={onClose}
                    />
                    <FunctionalButton
                        title={isSaving ? "Saving..." : "Save"}
                        className="text-h5 h-8 rounded-r5 border border-primary2 bg-primary2 px-4"
                        onClick={handleSave}
                        disabled={isSaving}
                    />
                </div>
            </div>
        </AnimatedModal>
    );
};

export default BehaviourRuleDialog;
